import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sparkles, Send, RotateCcw, ArrowRight, CornerDownLeft, X } from 'lucide-react'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { API_BASE_URL } from '@/lib/api'
import { useBrand, splitBrandName } from '@/hooks/useBrand'
import { useHelpChat } from './HelpChatContext'

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

const SUGGESTIONS = [
  'How do I create my first AI agent?',
  'How do I test a call?',
  'Where do I see my bookings?',
  'How do I invite my team?',
]

// Token format: {{LINK:/some/path|Button Label}}
const LINK_TOKEN_REGEX = /\{\{LINK:([^|}]+)\|([^}]+)\}\}/g

interface ParsedSegment {
  type: 'text' | 'link'
  text: string
  href?: string
}

function parseAssistantContent(content: string): ParsedSegment[] {
  const segments: ParsedSegment[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  LINK_TOKEN_REGEX.lastIndex = 0
  while ((match = LINK_TOKEN_REGEX.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', text: content.slice(lastIndex, match.index) })
    }
    segments.push({ type: 'link', text: match[2].trim(), href: match[1].trim() })
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < content.length) {
    segments.push({ type: 'text', text: content.slice(lastIndex) })
  }
  return segments
}

export function HelpChatPanel() {
  const { isOpen, closeHelp } = useHelpChat()
  const brand = useBrand()
  const brandBaseName = splitBrandName(brand.appName)[0]
  const navigate = useNavigate()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const abortRef = useRef<AbortController | null>(null)

  // Auto-scroll when messages update
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, streaming])

  // Cancel in-flight request on close
  useEffect(() => {
    if (!isOpen && abortRef.current) {
      abortRef.current.abort()
      abortRef.current = null
      setStreaming(false)
    }
  }, [isOpen])

  const handleDeepLink = useCallback(
    (href: string) => {
      closeHelp()
      navigate(href)
    },
    [closeHelp, navigate]
  )

  const sendMessage = useCallback(
    async (userText: string) => {
      const trimmed = userText.trim()
      if (!trimmed || streaming) return

      setError(null)
      const nextMessages: ChatMessage[] = [...messages, { role: 'user', content: trimmed }]
      setMessages(nextMessages)
      setInput('')
      setStreaming(true)

      // Placeholder assistant message we'll fill as tokens arrive
      setMessages(m => [...m, { role: 'assistant', content: '' }])

      const controller = new AbortController()
      abortRef.current = controller
      const token = localStorage.getItem('app_auth_token')

      try {
        const response = await fetch(`${API_BASE_URL}/api/help-chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token ? `Bearer ${token}` : '',
          },
          body: JSON.stringify({ messages: nextMessages }),
          signal: controller.signal,
        })

        if (!response.ok) {
          const text = await response.text().catch(() => '')
          let parsed: any = null
          try { parsed = text ? JSON.parse(text) : null } catch { /* ignore */ }
          const msg = parsed?.message || (response.status === 429
            ? 'You have hit the help chat rate limit. Try again in an hour.'
            : 'Something went wrong. Please try again.')
          setError(msg)
          setMessages(m => m.slice(0, -1)) // drop placeholder
          setStreaming(false)
          return
        }

        if (!response.body) {
          setError('No response body.')
          setMessages(m => m.slice(0, -1))
          setStreaming(false)
          return
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        while (true) {
          const { value, done } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })

          // Parse complete SSE events (separated by blank lines)
          const events = buffer.split('\n\n')
          buffer = events.pop() || ''

          for (const rawEvent of events) {
            const lines = rawEvent.split('\n')
            let eventName = 'message'
            let dataLine = ''
            for (const line of lines) {
              if (line.startsWith('event:')) eventName = line.slice(6).trim()
              else if (line.startsWith('data:')) dataLine = line.slice(5).trim()
            }
            if (!dataLine) continue

            try {
              const data = JSON.parse(dataLine)
              if (eventName === 'token' && data.text) {
                setMessages(m => {
                  const copy = [...m]
                  const last = copy[copy.length - 1]
                  if (last && last.role === 'assistant') {
                    copy[copy.length - 1] = { ...last, content: last.content + data.text }
                  }
                  return copy
                })
              } else if (eventName === 'error') {
                setError(data.message || 'Error generating response.')
              }
            } catch {
              // ignore malformed event
            }
          }
        }
      } catch (err: any) {
        if (err?.name === 'AbortError') return
        setError(err?.message || 'Network error.')
        setMessages(m => m.slice(0, -1))
      } finally {
        setStreaming(false)
        abortRef.current = null
      }
    },
    [messages, streaming]
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    sendMessage(input)
  }

  const resetConversation = () => {
    if (streaming && abortRef.current) abortRef.current.abort()
    setMessages([])
    setError(null)
    setInput('')
  }

  const showEmptyState = messages.length === 0 && !streaming

  return (
    <Sheet open={isOpen} onOpenChange={open => (open ? undefined : closeHelp())}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[440px] p-0 flex flex-col gap-0 overflow-hidden [&>button.absolute]:hidden"
      >
        {/* Header with gradient wash */}
        <SheetHeader
          className="relative px-4 py-4 border-b border-border/60 space-y-0 overflow-hidden bg-gradient-to-br from-brand-periwinkle/10 via-brand-royal/5 to-transparent"
        >
          {/* Subtle animated shine */}
          <div className="pointer-events-none absolute inset-0 opacity-60">
            <div className="absolute -top-8 -right-8 h-32 w-32 rounded-full bg-brand-royal/10 blur-2xl" />
          </div>
          <div className="relative flex items-center gap-3">
            <div className="relative">
              <div className="h-10 w-10 rounded-xl bg-gradient-brand flex items-center justify-center shadow-md shadow-brand-royal/40">
                <Sparkles className={cn('h-5 w-5 text-white', streaming && 'animate-pulse')} />
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-400 border-2 border-background shadow-sm" />
            </div>
            <div className="flex-1 min-w-0">
              <SheetTitle className="text-base leading-tight font-semibold">Ask {brandBaseName}</SheetTitle>
              <p className="text-xs text-muted-foreground leading-tight mt-0.5">
                {streaming ? 'Thinking...' : 'Your in-app guide'}
              </p>
            </div>
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={resetConversation}
                title="Start new conversation"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={closeHelp}
              title="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>

        {/* Messages area with subtle backdrop */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
          style={{
            background:
              'radial-gradient(ellipse at top, hsl(var(--primary) / 0.03) 0%, transparent 50%), hsl(var(--background))'
          }}
        >
          {showEmptyState && <EmptyState onPickSuggestion={sendMessage} />}

          {messages.map((msg, idx) => (
            <MessageBubble
              key={idx}
              message={msg}
              streaming={streaming && idx === messages.length - 1 && msg.role === 'assistant'}
              onDeepLink={handleDeepLink}
            />
          ))}

          {error && (
            <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>

        {/* Input with premium polish */}
        <form onSubmit={handleSubmit} className="border-t border-border/60 bg-card/80 backdrop-blur-sm p-3">
          <div
            className={cn(
              'flex items-end gap-2 rounded-2xl border bg-background p-1.5 transition-colors',
              'focus-within:border-brand-royal/50 focus-within:shadow-md focus-within:shadow-brand-royal/10',
              'border-input'
            )}
          >
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  sendMessage(input)
                }
              }}
              rows={1}
              placeholder={`Ask me anything about ${brandBaseName}...`}
              className="flex-1 resize-none bg-transparent px-2.5 py-2 text-sm focus:outline-none min-h-[36px] max-h-[120px] placeholder:text-muted-foreground/70"
              disabled={streaming}
            />
            <Button
              type="submit"
              size="icon"
              disabled={!input.trim() || streaming}
              className={cn(
                'h-9 w-9 rounded-xl shrink-0 transition-colors',
                input.trim() && !streaming
                  ? 'bg-gradient-brand hover:bg-gradient-brand-dark shadow-md shadow-brand-royal/30 hover:scale-105'
                  : 'bg-muted text-muted-foreground hover:bg-muted'
              )}
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <p className="mt-1.5 text-center text-[10px] text-muted-foreground/60">
            Press <kbd className="px-1 py-0.5 rounded bg-muted text-[9px]"><CornerDownLeft className="inline h-2.5 w-2.5" /></kbd> to send &middot; Shift + Enter for new line
          </p>
        </form>
      </SheetContent>
    </Sheet>
  )
}

function EmptyState({ onPickSuggestion }: { onPickSuggestion: (text: string) => void }) {
  const brand = useBrand()
  return (
    <div className="space-y-5 animate-fade-in-up">
      {/* Welcome card with gradient border */}
      <div className="relative rounded-2xl p-[1px] bg-gradient-to-br from-brand-royal/40 via-brand-indigo/20 to-transparent">
        <div className="rounded-2xl bg-card px-4 py-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 h-9 w-9 rounded-xl bg-gradient-brand flex items-center justify-center shadow-md shadow-brand-royal/30">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-foreground mb-1">How can I help?</p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                I'm {splitBrandName(brand.appName)[0]}'s in-app guide. Ask me how to do anything, and I'll walk you
                through it or take you right to the page you need.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Suggestion chips */}
      <div className="space-y-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80 px-1">
          Try asking
        </p>
        <div className="space-y-2">
          {SUGGESTIONS.map(s => (
            <button
              key={s}
              type="button"
              onClick={() => onPickSuggestion(s)}
              className={cn(
                'group w-full text-left rounded-xl border border-border/60 bg-card px-3.5 py-2.5 text-sm',
                'hover:bg-accent hover:border-brand-royal/40 hover:shadow-sm hover:shadow-brand-royal/10',
                'transition-colors flex items-center justify-between gap-2'
              )}
            >
              <span className="flex-1">{s}</span>
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/50 group-hover:text-brand-royal group-hover:translate-x-0.5 transition-colors shrink-0" />
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

function MessageBubble({
  message,
  streaming,
  onDeepLink,
}: {
  message: ChatMessage
  streaming: boolean
  onDeepLink: (href: string) => void
}) {
  const isUser = message.role === 'user'
  const segments = useMemo(
    () => (isUser ? [{ type: 'text' as const, text: message.content }] : parseAssistantContent(message.content)),
    [isUser, message.content]
  )

  if (isUser) {
    return (
      <div className="flex justify-end animate-fade-in-up">
        <div className="max-w-[85%] rounded-2xl rounded-br-md px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words bg-gradient-brand text-white shadow-md shadow-brand-royal/20">
          {segments.map((seg, idx) => <span key={idx}>{seg.text}</span>)}
        </div>
      </div>
    )
  }

  // Assistant
  return (
    <div className="flex items-end gap-2 animate-fade-in-up">
      <div className="shrink-0 h-7 w-7 rounded-lg bg-gradient-brand flex items-center justify-center shadow-sm shadow-brand-royal/30 mb-0.5">
        <Sparkles className={cn('h-3.5 w-3.5 text-white', streaming && 'animate-pulse')} />
      </div>
      <div
        className={cn(
          'max-w-[85%] rounded-2xl rounded-bl-md px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words',
          'bg-card border border-border/70 shadow-sm'
        )}
      >
        {segments.length === 0 && streaming && (
          <span className="inline-flex gap-1 py-1">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-royal/70 animate-bounce [animation-delay:-0.3s]" />
            <span className="h-1.5 w-1.5 rounded-full bg-brand-royal/70 animate-bounce [animation-delay:-0.15s]" />
            <span className="h-1.5 w-1.5 rounded-full bg-brand-royal/70 animate-bounce" />
          </span>
        )}
        {segments.map((seg, idx) =>
          seg.type === 'text' ? (
            <span key={idx}>{seg.text}</span>
          ) : (
            <button
              key={idx}
              type="button"
              onClick={() => seg.href && onDeepLink(seg.href)}
              className={cn(
                'inline-flex items-center gap-1.5 mx-0.5 my-1 rounded-lg px-2.5 py-1 text-xs font-semibold align-baseline transition-colors',
                'bg-gradient-to-r from-brand-royal to-brand-indigo text-white shadow-sm shadow-brand-royal/30',
                'hover:shadow-md hover:shadow-brand-royal/40 hover:scale-[1.02]'
              )}
            >
              {seg.text}
              <ArrowRight className="h-3 w-3" />
            </button>
          )
        )}
      </div>
    </div>
  )
}
